import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Spin, Alert, Select } from 'antd';
import clsx from 'clsx';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import EmissionIntensityLineChart from './IntensityChart';
import styles from './company.module.scss';
import { CompanyDashboardProps } from './constants';
import { SbtiDashboardResponse } from 'models/Sbti/sbtiYoy.model';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { deserialize } from 'serializr';
import { OrganizationRevenue } from 'models/OrganizationRevenue/organizationRevenue.model';
import { LocationRevenue } from 'models/LocationRevenue/locationRevenue.model';

const { Option } = Select;

interface EmissionIntensityChartProps {
  yoyData: SbtiDashboardResponse | undefined;
  yearEmissionLoading: boolean;
  parentID: number;
  locationOption: string;
  loadLine?: boolean;
  goodsData?: { year: number; totalWeight: number }[];
  revenueData?: {
    year: number;
    revenue: number | null;
    revenueUnit: string | null;
    revenueStatus: boolean;
  }[];
  loadingWeights?: boolean;
  loadingRevenue?: boolean;
}

const EmissionIntensityChart: React.FC<EmissionIntensityChartProps> = ({
  yoyData,
  yearEmissionLoading,
  parentID,
  locationOption,
  loadLine = false,
  goodsData: propsGoodsData = [],
  revenueData: propsRevenueData = [],
  loadingWeights: propsLoadingWeights = false,
  loadingRevenue: propsLoadingRevenue = false,
}) => {

  const [intensityType, setIntensityType] = useState<'weight' | 'revenue'>('revenue');
  const [selectedCurrency, setSelectedCurrency] = useState<'CAD' | 'USD' | 'EUR'>('CAD');
  const [revenueData, setRevenueData] = useState<{
    year: number;
    revenue: number | null;
    revenueUnit: string | null;
    revenueStatus: boolean;
  }[]>(propsRevenueData);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Use props data for goods (weight) - fetched in parent when Apply is clicked
  const goodsData = propsGoodsData;
  const loadingWeights = propsLoadingWeights;

  // Update revenueData when props change (from parent)
  useEffect(() => {
    setRevenueData(propsRevenueData);
  }, [propsRevenueData]);

  // Check for missing weight data errors
  // Only check when data or intensity type changes, not when locationOption changes
  useEffect(() => {
    if (intensityType === 'weight' && goodsData.length > 0) {
      const currentYear = new Date().getFullYear();
      const missingYears = goodsData
        .filter(
          (r) => r.year !== currentYear && (!r.totalWeight || r.totalWeight === 0)
        )
        .map((r) => r.year);

      if (missingYears.length > 0) {
        setErrorMessage(
          `Please fill in product weight data for year${missingYears.length > 1 ? 's' : ''
          } ${missingYears.join(', ')} for this location.`
        );
      } else {
        setErrorMessage(null);
      }
    } else if (intensityType === 'weight' && locationOption === CompanyDashboardProps.ALL) {
      setErrorMessage(CompanyDashboardProps.NO_LOCATION_CHOOSEN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intensityType, goodsData]); // Only depend on data changes, not locationOption

  // Fetch revenue data when currency changes (not when locationOption changes)
  // This allows currency switching without requiring Apply button
  useEffect(() => {
    const fetchRevenue = async () => {
      if (intensityType !== 'revenue') return;

      // Only refetch if currency is different from the default (CAD) that parent fetched
      // If currency is CAD, use props data
      if (selectedCurrency === 'CAD') {
        setRevenueData(propsRevenueData);
        return;
      }

      setErrorMessage(null);

      if (!yoyData?.yoyEmission || !parentID) {
        return;
      }

      setLoadingRevenue(true);
      const years = yoyData.yoyEmission.map((e) => e.year).filter((year): year is number => year !== undefined);

      // Organization-level revenue (rollup across all locations)
      if (locationOption === CompanyDashboardProps.ALL) {
        const results = await Promise.all(
          years.map(async (year) => {
            try {
              const response = await axiosInstance.get(ApiRoutes.GET_ORGANIZATION_REVENUE, {
                params: {
                  organization_id: parentID,
                  year,
                  currency: selectedCurrency,
                },
              });
              const orgRevenue = deserialize(OrganizationRevenue, response.data) as OrganizationRevenue;
              const revenueRaw = orgRevenue?.revenue;
              const revenueComplete = orgRevenue?.revenueComplete ?? false;
              const returnedUnit = orgRevenue?.revenueUnit ?? selectedCurrency ?? 'CAD';
            
              const revenueValue =
                revenueRaw === null || revenueRaw === undefined
                  ? null
                  : typeof revenueRaw === 'string'
                    ? (isNaN(parseFloat(revenueRaw)) ? null : parseFloat(revenueRaw))
                    : Number(revenueRaw);

              return {
                year,
                revenue: revenueValue,
                revenueUnit: returnedUnit,
                revenueStatus: revenueComplete,
              };
            } catch (err) {
              return { year, revenue: null, revenueUnit: null, revenueStatus: false };
            }
          })
        );

        const yearsWithMissing = results
          .filter(
            (r) =>
              !r.revenueStatus ||
              r.revenue === null ||
              r.revenue === undefined ||
              r.revenue === 0
          )
          .map((r) => r.year);

        if (yearsWithMissing.length > 0) {
          setErrorMessage(
            `Revenue data is incomplete for year${yearsWithMissing.length > 1 ? 's' : ''} ${yearsWithMissing.join(
              ', '
            )}. Please fill revenue data for all locations in these years.`
          );
        }

        setRevenueData(results);
        setLoadingRevenue(false);
        return;
      }

      // Location-level revenue (single location) - using new dedicated endpoint
      const results = await Promise.all(
        years.map(async (year) => {
          try {
            const response = await axiosInstance.get(ApiRoutes.GET_LOCATION_REVENUE, {
              params: {
                location_id: String(locationOption),
                year,
                currency: selectedCurrency,
              },
            });
            const locationRevenue = deserialize(LocationRevenue, response.data) as LocationRevenue;
            const revenueRaw = locationRevenue?.revenue;
            const revenueComplete = locationRevenue?.revenueStatus ?? false;
            const returnedUnit = locationRevenue?.revenueUnit ?? selectedCurrency ?? 'CAD';

            const revenueValue =
              revenueRaw === null || revenueRaw === undefined
                ? null
                : typeof revenueRaw === 'string'
                  ? (isNaN(parseFloat(revenueRaw)) ? null : parseFloat(revenueRaw))
                  : Number(revenueRaw);

            return {
              year,
              revenue: revenueValue,
              revenueUnit: returnedUnit,
              revenueStatus: revenueComplete,
            };
          } catch (err) {
            return { year, revenue: null, revenueUnit: null, revenueStatus: false };
          }
        })
      );

      const currentYear = new Date().getFullYear();
      const missingYears = results
        .filter(
          (r) =>
            r.year !== currentYear &&
            (!r.revenueStatus || r.revenue === null || r.revenue === undefined || r.revenue === 0)
        )
        .map((r) => r.year);

      if (missingYears.length > 0) {
        setErrorMessage(
          `Please fill in revenue data for year${missingYears.length > 1 ? 's' : ''
          } ${missingYears.join(', ')} for this location.`
        );
      }

      setRevenueData(results);
      setLoadingRevenue(false);
    };

    fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yoyData, parentID, intensityType, selectedCurrency, propsRevenueData]); // Only depend on currency change, not locationOption

  // Check for missing revenue data errors
  // Only check when data or intensity type changes, not when locationOption changes
  useEffect(() => {
    if (intensityType === 'revenue' && revenueData.length > 0) {
      if (locationOption === CompanyDashboardProps.ALL) {
        const yearsWithMissing = revenueData
          .filter(
            (r) =>
              !r.revenueStatus ||
              r.revenue === null ||
              r.revenue === undefined ||
              r.revenue === 0
          )
          .map((r) => r.year);

        if (yearsWithMissing.length > 0) {
          setErrorMessage(
            `Revenue data is incomplete for year${yearsWithMissing.length > 1 ? 's' : ''} ${yearsWithMissing.join(
              ', '
            )}. Please fill revenue data for all locations in these years.`
          );
        } else {
          setErrorMessage(null);
        }
      } else {
        const currentYear = new Date().getFullYear();
        const missingYears = revenueData
          .filter(
            (r) =>
              r.year !== currentYear &&
              (!r.revenueStatus || r.revenue === null || r.revenue === undefined || r.revenue === 0)
          )
          .map((r) => r.year);

        if (missingYears.length > 0) {
          setErrorMessage(
            `Please fill in revenue data for year${missingYears.length > 1 ? 's' : ''
            } ${missingYears.join(', ')} for this location.`
          );
        } else {
          setErrorMessage(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intensityType, revenueData]); // Only depend on data changes, not locationOption

  const intensityData = useMemo(() => {
    if (!yoyData?.yoyEmission) {
      return [];
    }

    if (intensityType === 'weight') {
      if (goodsData.length === 0) {
        return [];
      }

      return yoyData.yoyEmission
        .filter((entry) => entry.year !== undefined && entry.totalCO2e !== undefined)
        .map((entry) => {
          const year = entry.year!;
          const totalCO2e = entry.totalCO2e!;
          const goods = goodsData.find((g) => g.year === year)?.totalWeight ?? 0;
          const intensity =
            goods > 0
              ? Number(((totalCO2e * 1000) / goods).toFixed(2))
              : 0;
          return {
            year,
            intensity,
          };
        });
    } else {
      // intensityType === 'revenue'
      if (revenueData.length === 0) {
        return [];
      }

      return yoyData.yoyEmission
        .filter((entry) => entry.year !== undefined && entry.totalCO2e !== undefined)
        .map((entry) => {
          const year = entry.year!;
          const totalCO2e = entry.totalCO2e!;
          const revenueEntry = revenueData.find((r) => r.year === year);
          const revenue = revenueEntry?.revenue ?? null;

          // Calculate intensity - per million dollars
          // Formula: val = total_revenue / 1 million, then intensity = total_emission / val
          // Keep total_emission in tonnes (no kg conversion)
          let intensity = 0;
          if (revenue !== null && revenue !== undefined && revenue > 0) {
            const val = revenue / 1000000; // revenue in millions
            intensity = Number((totalCO2e / val).toFixed(2)); // totalCO2e is in tonnes, no conversion needed
          }

          return {
            year,
            intensity,
          };
        });
    }
  }, [yoyData, goodsData, revenueData, intensityType]);

  const combinedLoading = yearEmissionLoading || (intensityType === 'weight' ? loadingWeights : loadingRevenue);
  const showErrorState = !!errorMessage;
  const hasData = intensityData && intensityData.length > 0;
  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'USD': return '$';
      case 'CAD': return '$';
      default: return '$';
    }
  };

  const getRevenueUnitLabel = () => {
    return `tCO₂e per $MM`;
  };

  const intensityUnit = intensityType === 'weight'
    ? CompanyDashboardProps.INTENSITY_UNIT
    : getRevenueUnitLabel();

  return (
    <div className={styles.yoyEmissionArea}>
      <div className={styles.scopeDropdownArea}>
        <Row align="middle" gutter={[8, 0]} wrap={false}>
          <Col flex="0 0 auto">
            <h5 className={clsx(styles.yoyEmissionTitle, styles.extraMargin)}>
              {CompanyDashboardProps.INTENSITY_TEXT}
            </h5>
          </Col>
          <Col flex="0 0 auto">
            <Select
              suffixIcon={<Arrow fontSize={44} />}
              value={intensityType}
              onChange={(value: 'weight' | 'revenue') => setIntensityType(value)}
              className={styles.overlay}
              placeholder={CompanyDashboardProps.INTENSITY_TYPE_SELECT_PLACEHOLDER}
              style={{ minWidth: '140px' }}
            >
              <Option value="weight">
                {CompanyDashboardProps.INTENSITY_BY_WEIGHT}
              </Option>
              <Option value="revenue">
                {CompanyDashboardProps.INTENSITY_BY_REVENUE}
              </Option>
            </Select>
          </Col>
          {intensityType === 'revenue' && (
            <Col flex="0 0 auto">
              <Select
                value={selectedCurrency}
                onChange={(value: 'CAD' | 'USD' | 'EUR') => setSelectedCurrency(value)}
                className={styles.overlay}
                style={{ minWidth: '100px', maxWidth: '120px' }}
              >
                <Option value="CAD">CAD ($)</Option>
                <Option value="USD">USD ($)</Option>
                <Option value="EUR">EUR (€)</Option>
              </Select>
            </Col>
          )}
        </Row>
      </div>

      <Spin spinning={loadLine} tip="Loading">
        <div className={styles.cardContainer}>
          {showErrorState ? (
            <Alert
              message="Emission Intensity Info"
              description={errorMessage}
              type="warning"
              banner
            />
          ) : hasData ? (
            <div className={styles.breakdownChart}>
              {combinedLoading ? (
                <div className={styles.loaderContainer}>
                  <LoaderAnimation />
                </div>
              ) : (
                <EmissionIntensityLineChart
                  AxisProp={CompanyDashboardProps.YEAR}
                  unit={intensityUnit}
                  data={intensityData}
                  height={400}
                  loading={combinedLoading}
                />
              )}
            </div>
          ) : (
            <p className={styles.emptyText}>
              {CompanyDashboardProps.DATA_EMPTY}
            </p>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default EmissionIntensityChart;